import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    Alert,
    CircularProgress
} from '@mui/material';
import {
    PictureAsPdf as PdfIcon,
    Delete as DeleteIcon,
    Upload as UploadIcon,
    OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { projectFormsApi } from '../services/api';
import { renameFile } from '../utils/fileNaming';
import { limitFilesForUpload } from '../utils/uploadLimits';

const PdfUploadField = ({
    field,
    value = [],
    onChange,
    projectId,
    formId,
    disabled = false
}) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [pdfUrls, setPdfUrls] = useState({});
    const fileInputRef = useRef();
    const isPersistedPhotoId = (photoId) => /^\d+$/.test(String(photoId || ''));
    const resolveInlinePdfUrl = (pdf) => {
        if (!pdf) return '';
        if (typeof pdf.url === 'string' && pdf.url) return pdf.url;
        if (typeof pdf.blob_path === 'string' && /^https?:\/\//.test(pdf.blob_path)) return pdf.blob_path;
        return '';
    };

    // Load PDF URLs when PDFs change
    useEffect(() => {
        const loadPdfUrls = async () => {
            if (!value || value.length === 0) return;

            const newUrls = {};
            await Promise.all(
                value.map(async (pdf) => {
                    const inlineUrl = resolveInlinePdfUrl(pdf);
                    if (inlineUrl) {
                        newUrls[pdf.photo_id] = inlineUrl;
                        return;
                    }

                    if (isPersistedPhotoId(pdf.photo_id) && !pdfUrls[pdf.photo_id]) {
                        try {
                            const response = await projectFormsApi.getPhotoUrl(pdf.photo_id);
                            newUrls[pdf.photo_id] = response.data.url;
                        } catch (err) {
                            console.error(`Failed to load URL for PDF ${pdf.photo_id}:`, err);
                        }
                    }
                })
            );

            if (Object.keys(newUrls).length > 0) {
                setPdfUrls(prev => ({ ...prev, ...newUrls }));
            }
        };

        loadPdfUrls();
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFileSelect = async (event) => {
        const { files: selectedFiles, warning } = limitFilesForUpload(event.target.files || []);
        if (warning) {
            setError(warning);
            return;
        }

        if (selectedFiles.length === 0) return;

        const invalidFiles = selectedFiles.filter((file) =>
            (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) || file.size > 10 * 1024 * 1024
        );
        const validFiles = selectedFiles.filter((file) =>
            (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) && file.size <= 10 * 1024 * 1024
        );

        if (validFiles.length === 0) {
            setError(invalidFiles.some((file) => file.size > 10 * 1024 * 1024)
                ? 'PDF files must be under 10MB'
                : 'Please select PDF files');
            return;
        }

        try {
            setUploading(true);
            setError(invalidFiles.length > 0 ? 'Some files were skipped. Only PDFs under 10MB are supported.' : '');

            const uploadedPdfs = await Promise.all(validFiles.map(async (file) => {
                const renamedFile = renameFile(file);
                const formData = new FormData();
                formData.append('file', renamedFile);
                formData.append('field_id', field.id);

                const response = await projectFormsApi.uploadPhoto(projectId, formId, formData);

                return {
                    photo_id: response.data.photo_id,
                    file_name: response.data.file_name,
                    field_id: response.data.field_id
                };
            }));

            const updatedPdfs = [...(value || []), ...uploadedPdfs];
            onChange(field.id, updatedPdfs);

            const loadedUrls = {};
            await Promise.all(uploadedPdfs.map(async (pdf) => {
                try {
                    const urlResponse = await projectFormsApi.getPhotoUrl(pdf.photo_id);
                    loadedUrls[pdf.photo_id] = urlResponse.data.url;
                } catch (urlErr) {
                    console.error('Failed to load URL for new PDF:', urlErr);
                }
            }));

            if (Object.keys(loadedUrls).length > 0) {
                setPdfUrls(prev => ({
                    ...prev,
                    ...loadedUrls
                }));
            }

        } catch (err) {
            console.error('PDF upload error:', err);
            setError(err.response?.data?.error || 'Failed to upload PDF');
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleDeletePdf = async (photoId) => {
        try {
            await projectFormsApi.deletePhoto(photoId);
            const updatedPdfs = (value || []).filter(pdf => pdf.photo_id !== photoId);
            onChange(field.id, updatedPdfs);
            setPdfUrls(prev => {
                const next = { ...prev };
                delete next[photoId];
                return next;
            });
        } catch (err) {
            console.error('PDF delete error:', err);
            setError(err.response?.data?.error || 'Failed to delete PDF');
        }
    };

    const handleOpenPdf = async (photoId) => {
        try {
            const currentPdf = (value || []).find((pdf) => pdf.photo_id === photoId);
            let url = pdfUrls[photoId] || resolveInlinePdfUrl(currentPdf);
            if (!url) {
                if (!isPersistedPhotoId(photoId)) {
                    setError('This PDF is still syncing and cannot be opened yet.');
                    return;
                }
                const response = await projectFormsApi.getPhotoUrl(photoId);
                url = response.data.url;
                setPdfUrls(prev => ({ ...prev, [photoId]: url }));
            }
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (err) {
            console.error('PDF open error:', err);
            setError(err.response?.data?.error || 'Failed to open PDF');
        }
    };

    const pdfs = value || [];

    return (
        <Box>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {!disabled && (
                <Box sx={{ mb: 2 }}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        multiple
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                        disabled={disabled || uploading}
                    />
                    <Button
                        variant="outlined"
                        startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={disabled || uploading}
                    >
                        {uploading ? 'Uploading...' : 'Upload PDFs'}
                    </Button>
                </Box>
            )}

            {pdfs.length > 0 ? (
                <List dense sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    {pdfs.map((pdf, index) => (
                        <ListItem
                            key={pdf.photo_id}
                            divider={index < pdfs.length - 1}
                            sx={{ pr: disabled ? 1 : 8 }}
                        >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                <PdfIcon color="error" />
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            cursor: 'pointer',
                                            color: 'primary.main',
                                            '&:hover': { textDecoration: 'underline' },
                                            wordBreak: 'break-all'
                                        }}
                                        onClick={() => handleOpenPdf(pdf.photo_id)}
                                    >
                                        {pdf.file_name}
                                    </Typography>
                                }
                            />
                            <ListItemSecondaryAction>
                                <IconButton
                                    size="small"
                                    onClick={() => handleOpenPdf(pdf.photo_id)}
                                    title="Open PDF"
                                >
                                    <OpenInNewIcon fontSize="small" />
                                </IconButton>
                                {!disabled && (
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDeletePdf(pdf.photo_id)}
                                        color="error"
                                        title="Delete PDF"
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
            ) : (
                <Box sx={{
                    border: '2px dashed',
                    borderColor: 'divider',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.50',
                    borderRadius: 1
                }}>
                    <Typography color="text.secondary">No PDFs uploaded yet</Typography>
                </Box>
            )}
        </Box>
    );
};

export default PdfUploadField;
